import { TargetIcon, TrashIcon } from "@/assets/icons";
import EditField from "@/components/ui/editField";
import { MARKER_FOCUS_ZOOM_LEVEL } from "@/constants/map";
import { useMap } from "@/hooks/map/core/useMapContext";
import { MarkerPinItem } from "@/types/map";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

const TEST_ID_PREFIX = "catchment-marker-detail";

type Props = {
  data: MarkerPinItem[];
  className?: string;
  onRemoveItem: (item: MarkerPinItem) => void;
  onChangeMarkerName: (name: string, item: MarkerPinItem) => void;
};

const MarkerDetailList = ({
  className,
  data = [],
  onRemoveItem,
  onChangeMarkerName
}: Props) => {
  const { map } = useMap();

  const onFocusMarker = (item: MarkerPinItem) => {
    if (!map) return;
    map.flyTo({
      center: [item.lng, item.lat], // or [longitude, latitude] directly
      zoom: MARKER_FOCUS_ZOOM_LEVEL, // Adjust zoom level as needed
      duration: 2000 // Animation duration in milliseconds (optional)
      // other flyTo options: https://maplibre.org/maplibre-gl-js-docs/api/map/#flyto
    });
  };

  return (
    <div
      className={twMerge(
        "my-2 gap-1 flex flex-col",
        data.length > 3 ? "max-h-[150px] overflow-y-auto" : "",
        className
      )}
    >
      {data.map((item, index) => (
        <div
          key={item.id}
          className="flex justify-between items-center h-[52px] px-2 rounded-[12px] border border-gainsboro gap-2"
        >
          {/* Info */}
          <div className="cancel-draggable flex flex-col flex-grow overflow-hidden">
            <EditField
              inputFieldName={`${TEST_ID_PREFIX}-name-${index}`}
              className="px-0"
              textClassName="text-txtHeading"
              value={item.locationName || `Marker #${index + 1}`}
              onChange={(text) => onChangeMarkerName(text, item)}
              testId={`${TEST_ID_PREFIX}-${index}-name`}
            />
            <span className="text-[10px] text-txtPlaceholder">
              {item.lat}, {item.lng}
            </span>
          </div>
          {/* Action */}
          <div className="flex gap-1 items-center">
            <Button
              variant="ghost"
              onClick={() => onRemoveItem(item)}
              type="button"
              data-testid={`${TEST_ID_PREFIX}-${index}-delete-button`}
            >
              <TrashIcon className="fill-txtBody" />
            </Button>
            <Button
              variant="ghost"
              onClick={() => onFocusMarker(item)}
              type="button"
              data-testid={`${TEST_ID_PREFIX}-${index}-focus-button`}
            >
              <TargetIcon />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MarkerDetailList;
